import { Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { BotBaseSchema } from '@/@types/schemas/bots'
import { FeedbackError } from '@/components/feedbacks'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { FormField, FormItem, FormLabel } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { useCompany } from '@/contexts/company'
import useTranslator from '@/hooks/translation'
import { randomPattern } from '@/utils/randoms'

import { ProjectsCreateSchemaType } from '../types'

export function ProjectsCreateStepTwo() {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<ProjectsCreateSchemaType>()

  const { bots: companyBots, botsStatus, loadBots } = useCompany()

  useEffect(() => {
    loadBots()
  }, [loadBots])

  const { getTranslation } = useTranslator()

  const botIds = useWatch({ control, name: 'botIds' })

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')

  const handleAddBot = (bot: BotBaseSchema) => {
    setValue('botIds', [...botIds, bot._id])
    setSearchTerm('')
  }

  const handleRemoveBot = (botId: string) => {
    setValue(
      'botIds',
      botIds.filter((id) => id !== botId),
    )
  }

  const filteredBots = useMemo(() => {
    const searchTermCleaned = searchTerm.trim().toLowerCase()

    const unselectedBots = companyBots.filter(
      (bot) => !botIds.includes(bot._id),
    )

    if (!searchTermCleaned) return unselectedBots

    return unselectedBots.filter((bot) =>
      bot.name.toLowerCase().includes(searchTermCleaned),
    )
  }, [companyBots, botIds, searchTerm])

  return (
    <>
      <div className="p-6 px-8">
        <FormField
          control={control}
          name="botIds"
          render={() => (
            <FormItem>
              <FormLabel className="text-xs md:text-sm">
                {getTranslation('projects.modal.addBots')}
              </FormLabel>

              <div className="space-y-1">
                <div className="flex items-center gap-2 align-top">
                  <div className="flex w-full flex-col gap-2">
                    <Popover
                      open={isOpen}
                      onOpenChange={setIsOpen}
                      modal={true}
                    >
                      <PopoverTrigger asChild>
                        <Input
                          type="text"
                          role="combobox"
                          aria-expanded={isOpen}
                          aria-controls="bot-listbox"
                          aria-autocomplete="list"
                          aria-haspopup="listbox"
                          value={searchTerm}
                          placeholder={getTranslation('common.searchBots')}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          autoComplete="off"
                        />
                      </PopoverTrigger>

                      <PopoverContent
                        align="start"
                        className="h-fit w-[--radix-popover-trigger-width] p-0"
                      >
                        {botsStatus.type === 'loading' ? (
                          <div className="flex flex-col gap-2 p-2">
                            {[0, 1, 2].map((index) => (
                              <div key={index} className="h-12">
                                <Skeleton className="h-full w-full" />
                              </div>
                            ))}
                          </div>
                        ) : botsStatus.type === 'error' ? (
                          <div className="p-2">
                            <FeedbackError
                              layout="compact"
                              errorType={botsStatus.errorType}
                              eventId={botsStatus.eventId}
                              onRetry={loadBots}
                              isRetrying={botsStatus.isRetrying}
                            />
                          </div>
                        ) : (
                          <Command>
                            <CommandList
                              id="bot-listbox"
                              className="max-h-[200px] min-h-14 overflow-y-auto"
                            >
                              <CommandGroup>
                                {filteredBots && filteredBots?.length > 0 ? (
                                  filteredBots?.map((bot) => (
                                    <CommandItem
                                      key={bot._id}
                                      value={bot.name}
                                      aria-selected={botIds.includes(bot._id)}
                                      role="option"
                                      className="h-14 cursor-pointer"
                                      onSelect={() => {
                                        handleAddBot(bot)
                                        setIsOpen(false)
                                      }}
                                    >
                                      <Avatar className="h-9 w-9">
                                        <AvatarImage
                                          src={bot?.picture || ''}
                                          alt={bot?.name}
                                          className="object-cover"
                                        />

                                        <AvatarFallback>
                                          {randomPattern('avatar', bot.name)}
                                        </AvatarFallback>
                                      </Avatar>

                                      {bot.name}
                                    </CommandItem>
                                  ))
                                ) : (
                                  <CommandEmpty>
                                    {getTranslation('common.noResults')}
                                  </CommandEmpty>
                                )}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {errors.botIds?.message && (
                  <span className="pl-2 text-xs font-medium text-destructive">
                    {getTranslation('common.validations.requiredValue')}
                  </span>
                )}
              </div>
            </FormItem>
          )}
        />
      </div>

      <FormLabel className="mb-2 mt-8 flex px-8 text-xs md:text-sm">
        {getTranslation('bots.added')}
      </FormLabel>

      <Separator />

      <div className="my-4 flex h-36 max-h-36 min-h-36 flex-col gap-2 overflow-y-auto px-8">
        {companyBots
          .filter((bot) => botIds.includes(bot._id))
          .map((bot) => (
            <div
              key={bot._id}
              className="flex h-14 max-h-14 min-h-14 items-center gap-2"
            >
              <Avatar className="h-10 w-10">
                <AvatarImage src={bot.picture} alt={bot.name} />
                <AvatarFallback>
                  {randomPattern('avatar', bot.name)}
                </AvatarFallback>
              </Avatar>

              <span>{bot.name}</span>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-auto"
                onClick={() => handleRemoveBot(bot._id)}
              >
                <Trash2 />
              </Button>
            </div>
          ))}
      </div>
    </>
  )
}
